import React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import queryString from 'query-string';
import { getBooleanConfig } from '@zg-rentals/frontend-config';
import ChecklistPage from './ChecklistPage';
import EmptyLeasePage from './EmptyLeasePage';
import RentalProtectionAreYouSurePage from './RentalProtectionAreYouSurePage';
import RentalProtectionIneligibleLeaseSelectedPage from './RentalProtectionIneligibleLeaseSelected';
import RentalProtectionUnsignedLeaseWarningPage from './RentalProtectionUnsignedLeaseWarning';
import MoveInCostsPage from './MoveInCostsPage';
import RentAmountPage from './RentAmountPage';
import RentalProtectionDifferentValueThanLeaseWarningPage from './RentalProtectionDifferentValueWarning';
import DueDatePage from './DueDatePage';
import TermLengthPage from './TermLengthPage';
import RentalProtectionDifferentTermThanLeaseWarningPage from './RentalProtectionDifferentTermWarning';
import AccountInfoPage from './AccountInfoPage';
import InviteTenantPage from './InviteTenantPage';
import LeaseSelectorPage from './LeaseSelectorPage';
import TaxVerificationPage from './TaxVerificationPage';
import PhoneVerificationPage from './PhoneVerificationPage';
import KnowledgeBasedAuthenticationPage from './KnowledgeBasedAuthenticationPage';
import ConfirmationPage from './ConfirmationPage';
import PostConfirmationPage from './PostConfirmationPage';
import ExitPaymentSetupPage from './ExitPaymentSetup';
import paths from '@rental-manager/pathsManifest';
import { RouteWithAuth } from '@common/components/RouteWithAuth';
import { IClusterSettingsModel } from '../../models/Types';
import { Location } from 'history';

type Props = {
  baseUrl: string;
  listing: Record<string, unknown>;
  propertyId: string;
  landlordStatus: string;
  clusterSettings: IClusterSettingsModel;
  router: Record<string, unknown>;
  acceptTerms: boolean;
  location: Location;
};

class OnboardingModalRoutes extends React.Component<Props> {
  static defaultProps = {
    acceptTerms: false,
  };

  modalProps: {
    baseUrl: string;
    listing: Record<string, unknown>;
    propertyId: string;
    landlordStatus: string;
    clusterSettings: IClusterSettingsModel;
    acceptTerms: boolean;
  };

  constructor(props) {
    super(props);
    this.modalProps = {
      baseUrl: props.baseUrl,
      listing: props.listing,
      propertyId: props.propertyId,
      landlordStatus: props.landlordStatus,
      clusterSettings: props.clusterSettings,
      acceptTerms: props.acceptTerms,
    };
  }

  ChecklistRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.checklistPage}
      exact
      render={(routeProps) => {
        return <ChecklistPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  EmptyLeaseRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.emptyLeasePage}
      exact
      render={(routeProps) => {
        return <EmptyLeasePage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  RentalProtectionAreYouSureRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.areYouSurePage}
      exact
      render={(routeProps) => {
        return <RentalProtectionAreYouSurePage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  RentalProtectionIneligibleLeaseSelectedRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.ineligibleLeaseSelected}
      exact
      render={(routeProps) => {
        return <RentalProtectionIneligibleLeaseSelectedPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  RentalProtectionUnsignedLeaseWarningRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.unsignedLeaseWarning}
      exact
      render={(routeProps) => {
        return <RentalProtectionUnsignedLeaseWarningPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  RentalProtectionDifferentValueThanLeaseWarningRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.differentValueThanLeaseWarningPage}
      exact
      render={(routeProps) => {
        return <RentalProtectionDifferentValueThanLeaseWarningPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  RentalProtectionDifferentTermThanLeaseWarningRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.differentTermThanLeaseWarningPage}
      exact
      render={(routeProps) => {
        return <RentalProtectionDifferentTermThanLeaseWarningPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  LeaseSelectorRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.leaseSelectorPage}
      exact
      render={(routeProps) => {
        const searchString = routeProps.location.search;
        const queryParams = queryString.parse(searchString);
        const acceptTermsParam = queryParams.acceptTerms;

        // Redirect if user has not clicked "accept terms"
        if (!this.props.acceptTerms && acceptTermsParam !== 'true') {
          return <Redirect to={{ pathname: this.props.baseUrl, search: '' }} />;
        }

        return <LeaseSelectorPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  RentAmountRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.rentAmountPage}
      exact
      render={(routeProps) => {
        const searchString = routeProps.location.search;
        const queryParams = queryString.parse(searchString);
        const acceptTermsParam = queryParams.acceptTerms;

        // Redirect if user has not clicked "accept terms"
        if (!this.props.acceptTerms && acceptTermsParam !== 'true') {
          return <Redirect to={{ pathname: this.props.baseUrl, search: '' }} />;
        }

        return <RentAmountPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  DueDateRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.dueDatePage}
      exact
      render={(routeProps) => {
        const searchString = routeProps.location.search;
        const queryParams = queryString.parse(searchString);
        const acceptTermsParam = queryParams.acceptTerms;

        // Redirect if user has not clicked "accept terms"
        if (!this.props.acceptTerms && acceptTermsParam !== 'true') {
          return <Redirect to={{ pathname: this.props.baseUrl, search: '' }} />;
        }

        return <DueDatePage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  TermLengthRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.termLengthPage}
      exact
      render={(routeProps) => {
        const searchString = routeProps.location.search;
        const queryParams = queryString.parse(searchString);
        const acceptTermsParam = queryParams.acceptTerms;

        // Redirect if user has not clicked "accept terms"
        if (!this.props.acceptTerms && acceptTermsParam !== 'true') {
          return <Redirect to={{ pathname: this.props.baseUrl, search: '' }} />;
        }

        return <TermLengthPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  AccountInfoRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.accountInfoPage}
      exact
      render={(routeProps) => {
        return <AccountInfoPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );
  MoveInCostsRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.moveInCostsPage}
      exact
      render={(routeProps) => {
        return <MoveInCostsPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  InviteTenantRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.inviteTenantPage}
      exact
      render={(routeProps) => {
        return <InviteTenantPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  TaxVerificationRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.taxVerificationPage}
      exact
      render={(routeProps) => {
        return <TaxVerificationPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  PhoneVerificationRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.phoneVerificationPage}
      exact
      render={(routeProps) => {
        return <PhoneVerificationPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  KnowledgeBasedAuthenticationRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.knowledgeBasedAuthenticationPage}
      exact
      render={(routeProps) => {
        return <KnowledgeBasedAuthenticationPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  OnboardingConfirmationRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.confirmationPage}
      exact
      render={(routeProps) => {
        return <ConfirmationPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  PostConfirmationRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.postConfirmationPage}
      exact
      render={(routeProps) => {
        return <PostConfirmationPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  ExitPaymentSetupRoute = (): React.ReactNode => (
    <RouteWithAuth
      path={this.props.baseUrl + paths.onboarding.exitPaymentSetup}
      exact
      render={(routeProps) => {
        return <ExitPaymentSetupPage {...routeProps} {...this.modalProps} />;
      }}
    />
  );

  CatchAllRoute = (): React.ReactNode => (
    <Route
      path="*"
      render={(routeProps) => {
        if (routeProps.location.pathname !== this.props.baseUrl) {
          return <Redirect to={{ pathname: this.props.baseUrl, search: '' }} />;
        }
        return null;
      }}
    />
  );

  componentDidMount() {
    const isVisualRefreshEnabled = getBooleanConfig('isVisualRefreshEnabled', false);
    if (isVisualRefreshEnabled) {
      document.documentElement.setAttribute('data-panda-theme', 'zillow');
    }
  }

  componentWillUnmount() {
    document.documentElement.setAttribute('data-panda-theme', 'legacy-zillow');
  }

  render(): React.ReactNode {
    const { acceptTerms, location } = this.props;

    return (
      <Switch location={location}>
        {this.ChecklistRoute()}
        {acceptTerms && this.EmptyLeaseRoute()}
        {acceptTerms && this.LeaseSelectorRoute()}
        {acceptTerms && this.RentalProtectionAreYouSureRoute()}
        {acceptTerms && this.RentalProtectionIneligibleLeaseSelectedRoute()}
        {acceptTerms && this.RentalProtectionUnsignedLeaseWarningRoute()}
        {acceptTerms && this.RentAmountRoute()}
        {acceptTerms && this.RentalProtectionDifferentValueThanLeaseWarningRoute()}
        {acceptTerms && this.ExitPaymentSetupRoute()}
        {acceptTerms && this.DueDateRoute()}
        {acceptTerms && this.TermLengthRoute()}
        {acceptTerms && this.RentalProtectionDifferentTermThanLeaseWarningRoute()}
        {acceptTerms && this.AccountInfoRoute()}
        {acceptTerms && this.InviteTenantRoute()}
        {acceptTerms && this.TaxVerificationRoute()}
        {acceptTerms && this.PhoneVerificationRoute()}
        {acceptTerms && this.MoveInCostsRoute()}
        {acceptTerms && this.KnowledgeBasedAuthenticationRoute()}
        {acceptTerms && this.OnboardingConfirmationRoute()}
        {acceptTerms && this.PostConfirmationRoute()}
        {this.CatchAllRoute()}
      </Switch>
    );
  }
}

export default connect((state: { router }) => ({ location: state.router.location }))(OnboardingModalRoutes);
